import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { seoQueues } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";

export const Route = createFileRoute("/admin/seo/internal-links")({
  head: () => ({ meta: [{ title: "Ranking queues · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: QueuesPage,
});

function pathOf(url?: string | null) {
  if (!url) return "—";
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function QueuesPage() {
  const load = useServerFn(seoQueues);
  const q = useQuery({ queryKey: ["seo-queues"], queryFn: () => load() });
  const d = q.data as any;

  const table = (rows: any[]) => (
    <DataTable
      head={["Search", "Page", "Position", "Impressions", "Clicks"]}
      rows={(rows ?? []).map((r) => [
        r.query,
        pathOf(r.page),
        r.position.toFixed(1),
        num(r.impressions),
        num(r.clicks),
      ])}
    />
  );

  return (
    <SeoPage
      title="Ranking opportunities"
      description="Searches where we already appear and could realistically move up. Supporting internal links and deeper coverage of the exact question are the usual fixes."
    >
      {q.isLoading ? (
        <Loading />
      ) : !d?.diagnosis?.hasData ? (
        <Empty>No Search Console data collected yet, so there is nothing to rank-order.</Empty>
      ) : (
        <div className="grid gap-4">
          <Panel title="Striking distance — positions 4 to 10" subtitle="Closest to the top three.">
            {table(d.queues.top3)}
          </Panel>
          <Panel title="Just off page one — positions 11 to 20">{table(d.queues.page1)}</Panel>
          <Panel title="Lift candidates — positions 21 to 50">{table(d.queues.lift)}</Panel>
          <Panel title="Pages competing with each other" subtitle="Pick one main page per search and differentiate the rest.">
            {d.diagnosis.cannibalization?.length ? (
              <DataTable
                head={["Search", "Competing pages"]}
                rows={d.diagnosis.cannibalization.map((c: any) => [
                  c.query,
                  c.pages.map((p: any) => pathOf(p.page)).join(", "),
                ])}
              />
            ) : (
              <Empty>No overlaps detected.</Empty>
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
